/*eslint complexity: off*/
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Input from '@material-ui/core/Input';
import LinearProgress from '~/components/LinearProgress';

import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import InputControl from '~/components/Input/InputControl';
import InputNumber from '~/components/Input/InputNumber';
import InputSelect from '~/components/Input/InputSelect';
import Button from '~/components/Button';
import InputSelectAsync from '~/components/Input/InputSelectAsync';

import MedicalRecordRecord from '~/records/MedicalRecordRecord';
import Request from '~/providers/Request';
import isPresent from '~/utils/isPresent';
import { fetchCities } from '~/utils/fetchCities';

import { maritalStatusOptions } from '~/config/constants';

const attrName = (attr) => MedicalRecordRecord.hummanAttributeName(attr);

function MedicalRecordForm({
  record,
  setRecord,
  handleChange,
  onSubmit,
  onCancel,
  loading,
}) {
  const { t } = useTranslation();
  const [doctorsOptions, setDoctorsOptions] = useState([]);

  useEffect(() => {
    async function fetchDoctors() {
      Request.get('/admin/users_doctors').then((result) => {
        const items = result.data.map((user) => {
          return {
            id: Number(user.id),
            name: `${user.name}${
              isPresent(user.specialties)
                ? ` <${user.specialties[0].name}>`
                : ' <Clínico Geral>'
            }`,
          };
        });
        setDoctorsOptions(items);
      });
    }
    fetchDoctors();
  }, []);

  return (
    <>
      <LinearProgress loading={loading} />

      <GridContainer>
        <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
          <InputControl
            name={'doctor'}
            required
            value={record.get('doctor')}
            label={attrName('doctor')}
            inputComponent={InputSelect}
            onChange={({ doctor }) => {
              setRecord(record.updateAttributes({ doctor: doctor }));
            }}
            InputProps={{
              isClearable: true,
              options: doctorsOptions,
              outValueType: 'object',
            }}
          />
        </GridItem>

        <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
          <InputControl
            name={'religion'}
            value={record.get('religion')}
            label={attrName('religion')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'health_insurance'}
            value={record.get('health_insurance')}
            label={attrName('health_insurance')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'medical_plan'}
            value={record.get('medical_plan')}
            label={attrName('medical_plan')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'medical_number'}
            value={record.get('medical_number')}
            label={attrName('medical_number')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
          <InputControl
            value={record.get('weight')}
            name="weight"
            label={attrName('weight')}
            inputComponent={InputNumber}
            InputProps={{
              config: { allowDecimal: true, requireDecimal: true },
            }}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
          <InputControl
            value={record.get('height')}
            name="height"
            label={attrName('height')}
            inputComponent={InputNumber}
            InputProps={{
              config: { allowDecimal: true, requireDecimal: true },
            }}
            onChange={handleChange}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'rg'}
            value={record.get('rg')}
            label={attrName('rg')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'profession'}
            value={record.get('profession')}
            label={attrName('profession')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'father_name'}
            value={record.get('father_name')}
            label={attrName('father_name')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'mother_name'}
            value={record.get('mother_name')}
            label={attrName('mother_name')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'kinship'}
            value={record.get('kinship')}
            label={attrName('kinship')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'family_contact'}
            value={record.get('family_contact')}
            label={attrName('family_contact')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'naturalness'}
            value={record.get('naturalness')}
            label={attrName('naturalness')}
            inputComponent={InputSelectAsync}
            onChange={({ naturalness }) => {
              setRecord(record.updateAttributes({ naturalness: naturalness }));
            }}
            InputProps={{
              isClearable: true,
              handleFetch: fetchCities,
              createEnabled: false,
              error: false,
            }}
          />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            label={attrName('marital_status')}
            value={record.get('marital_status')}
            inputComponent={InputSelect}
            name={'marital_status'}
            onChange={({ marital_status }) => {
              setRecord(record.updateAttributes({ marital_status }));
            }}
            InputProps={{
              isClearable: true,
              outValueType: 'string',
              options: Object.keys(maritalStatusOptions).map((key) => ({
                id: key,
                name: maritalStatusOptions[key],
              })),
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
          <InputControl
            name={'medication_in_use'}
            value={record.get('medication_in_use')}
            label={attrName('medication_in_use')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
          <InputControl
            name={'additional_information'}
            value={record.get('additional_information')}
            label={attrName('additional_information')}
            inputComponent={Input}
            onChange={handleChange}
            InputProps={{
              multiline: true,
              rows: '4',
            }}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          style={{ display: 'block', textAlign: 'right' }}
        >
          <Button style={{ margin: 5 }} color="grey" onClick={onCancel}>
            {t('action.cancel')}
          </Button>
          <Button color="green" onClick={onSubmit}>
            {t('action.save')}
          </Button>
        </GridItem>
      </GridContainer>
      <LinearProgress loading={loading} />
    </>
  );
}

MedicalRecordForm.propTypes = {
  record: PropTypes.instanceOf(MedicalRecordRecord).isRequired,
  handleChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  setRecord: PropTypes.func.isRequired,
};

MedicalRecordForm.defaultProps = {
  loading: false,
};

export default React.memo(MedicalRecordForm);
